import React, {
    useCallback,
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalDialog,
    ModalHeader,
    Button,
} from 'arag-ui-lib';
import { useTermsAndConditions } from '../../hooks/useTermsAndConditions';
import styles from './termsWithLanguageModal.module.scss';
import { RootState } from '../../store/store';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../actions';
import LanguageIcon from '../icons/languageIcon';
import codes from '../../utils/constants/codes';

type Props = {
    areTermsConsented: boolean;
    isModalOpen: boolean;
};

const TermsWithLanguageModal = (props: Props): React.ReactElement => {
    const { isModalOpen, areTermsConsented } = props;

    const dispatch = useDispatch();
    const [userHasReviewed, setUserHasReviewed] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>('en-GB'); // Default to English

    const currentCountry = useSelector(
        (state: RootState) =>
            state.applicationSettings.apiApplicationSettings.currentCountryCode,
    );
    const userId = useSelector(
        (state: RootState) => state.applicationUser.applicationUserDetails.id,
    );
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // Marks the end of the terms text; see the IntersectionObserver below.
    const endOfTermsRef = useRef<HTMLDivElement>(null);

    // Detect browser language on mount
    useEffect(() => {
        const lang = navigator.language;
        setSelectedLanguage(lang);
    }, []);

    // Fetch terms only when showTerms is true and a language is selected
    const { data: termsAndConditions } = useTermsAndConditions(
        areTermsConsented,
        currentCountry,
        userId,
        selectedLanguage,
    );

    const updateApplicationTermsAndConditionsTermsConsentedAction = useCallback(
        (payload: any) =>
            dispatch(
                actions.updateApplicationTermsAndConditionsTermsConsented(
                    payload,
                ),
            ),
        [dispatch],
    );


    const fitModalToViewport = useCallback(() => {
        const visual = (window as any).visualViewport;
        const viewport: number = visual ? visual.height : window.innerHeight;

        // Keep the library's 8rem offset on tall viewports, collapse toward the
        // top edge on short ones. Whole pixels, so the padding we set and the
        // height we subtract stay exactly consistent.
        const offset = Math.round(Math.min(Math.max(16, viewport * 0.1), 128));
        const gap = 24;
        const available = Math.round(Math.max(160, viewport - offset - gap));

        const overlay = document.getElementById(
            'display_termsModalWithLanguage_modal_wrapper',
        );
        const dialog = document.getElementById(
            'display_termsModalWithLanguage_dialog',
        );
        const content = document.getElementById(
            'display_termsModalWithLanguage_content',
        );
        const body = document.getElementById(
            'display_termsModalWithLanguage_wrapper',
        );
        const text = scrollContainerRef.current;

        if (overlay) {
            // The overlay is fixed with top/bottom 0, so it already spans the
            // visible viewport. Keep it scrollable as a last-resort escape hatch.
            overlay.style.paddingTop = `${offset}px`;
            overlay.style.paddingBottom = `${gap}px`;
            overlay.style.overflowY = 'auto';

            // Publish the measured numbers as custom properties as well. The
            // stylesheet documents these as its own cap; they inherit down to
            // the dialog and content. Without them that rule resolves to its
            // `none` fallback and the layer it describes does not exist.
            overlay.style.setProperty('--terms-modal-offset', `${offset}px`);
            overlay.style.setProperty(
                '--terms-modal-available',
                `${available}px`,
            );
        }

        if (dialog) {
            dialog.style.marginTop = '0px';
            dialog.style.marginBottom = '0px';
            dialog.style.maxHeight = `${available}px`;
            dialog.style.display = 'flex';
            dialog.style.flexDirection = 'column';
            dialog.style.minHeight = '0px';
        }

        if (content) {
            content.style.maxHeight = `${available}px`;
            content.style.display = 'flex';
            content.style.flexDirection = 'column';
            content.style.minHeight = '0px';
        }

        if (body) {
            body.style.display = 'flex';
            body.style.flexDirection = 'column';
            body.style.flex = '1 1 auto';
            body.style.minHeight = '0px';
            // auto rather than hidden: if anything ever does overflow here it
            // stays reachable by scrolling instead of being clipped away.
            body.style.overflowY = 'auto';
        }

        if (text) {
            // min-height 0 is what lets this shrink below its content height and
            // become the scroller, instead of pushing the footer out the bottom.
            text.style.flex = '1 1 auto';
            text.style.minHeight = '0px';
            text.style.overflowY = 'auto';

            // Nothing may stop a touch pan from chaining out of this region.
            // `overscroll-behavior: contain` here broke scrolling outright on
            // Surface and iPad: with no scrollbar to drag, panning is the only
            // way to scroll, and containment left the pan with nowhere to go.
            (text.style as any).overscrollBehavior = 'auto';
            (text.style as any).touchAction = 'pan-y';
        }
    }, []);

    useLayoutEffect(() => {
        if (areTermsConsented || !termsAndConditions) return;

        fitModalToViewport();

        const visual = (window as any).visualViewport;
        window.addEventListener('resize', fitModalToViewport);
        window.addEventListener('orientationchange', fitModalToViewport);
        if (visual) {
            visual.addEventListener('resize', fitModalToViewport);
        }

        return () => {
            window.removeEventListener('resize', fitModalToViewport);
            window.removeEventListener('orientationchange', fitModalToViewport);
            if (visual) {
                visual.removeEventListener('resize', fitModalToViewport);
            }
        };
    }, [areTermsConsented, termsAndConditions, showTerms, fitModalToViewport]);

    // Start each new set of terms unreviewed.
    useEffect(() => {
        if (!showTerms || !termsAndConditions) return;
        setUserHasReviewed(false);
    }, [showTerms, termsAndConditions]);


    useEffect(() => {
        if (!showTerms || !termsAndConditions) return;

        const sentinel = endOfTermsRef.current;
        if (!sentinel) return;

        // Fail open rather than trap the user behind a button that can never
        // enable. Consent stays explicit - they still have to click Accept.
        if (typeof IntersectionObserver === 'undefined') {
            setUserHasReviewed(true);
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    setUserHasReviewed(true);
                }
            },
            { threshold: 0 },
        );

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [showTerms, termsAndConditions]);

    const handleAgreeClick = () => {
        updateApplicationTermsAndConditionsTermsConsentedAction({
            userId: userId,
            languageCode: selectedLanguage,
        });
    };

    const handleLanguageSelect = (lang: string) => {
        setSelectedLanguage(lang);
        setShowTerms(true);
    };

    return (
        <>
            {!areTermsConsented && termsAndConditions !== null ? (
                <>
                    <div className={styles['modal-wrapper']}>
                        <Modal
                            id={'display_termsModalWithLanguage_modal'}
                            fadeIn={isModalOpen}
                            darkenedBackground
                            noScroll={true}
                        >
                            <ModalDialog
                                id={'display_termsModalWithLanguage_dialog'}
                                size={!showTerms ? 'small' : 'lg'}
                            >
                                <ModalContent
                                    id={
                                        'display_termsModalWithLanguage_content'
                                    }
                                >
                                    <ModalHeader
                                        id={
                                            'display_termsModalWithLanguage_header'
                                        }
                                        title={''}
                                    />
                                    <ModalBody
                                        id={
                                            'display_termsModalWithLanguage_wrapper'
                                        }
                                    >
                                        {!showTerms ? (

                                            <div
                                                className={
                                                    styles['language-step']
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles['language-body']
                                                    }
                                                >
                                                    <LanguageIcon
                                                        width={48}
                                                        height={48}
                                                        stroke="#000"
                                                        style={{
                                                            marginBottom: 12,
                                                            flex: '0 0 auto',
                                                        }}
                                                    />
                                                    <div
                                                        style={{
                                                            fontWeight: 'bold',
                                                            fontSize: 18,
                                                            marginBottom: 8,
                                                        }}
                                                    >
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: termsAndConditions?.languageModalTitle,
                                                            }}
                                                        />
                                                    </div>
                                                    <div
                                                        style={{
                                                            marginBottom: 16,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: termsAndConditions?.languageModalBody,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className={
                                                        styles[
                                                        'language-actions'
                                                        ]
                                                    }
                                                >
                                                    <Button
                                                        variant="primary"
                                                        label={
                                                            termsAndConditions?.languageModalButtonContinueText
                                                        }
                                                        onClick={() =>
                                                            handleLanguageSelect(
                                                                selectedLanguage ==
                                                                    codes.languageFrCaCode
                                                                    ? selectedLanguage
                                                                    : codes.languageEnCaCode,
                                                            )
                                                        }
                                                    />
                                                    <Button
                                                        variant="primary"
                                                        label={
                                                            termsAndConditions?.languageModalButtonSwitchText
                                                        }
                                                        onClick={() =>
                                                            handleLanguageSelect(
                                                                selectedLanguage ===
                                                                    codes.languageEnCaCode ||
                                                                    selectedLanguage !==
                                                                    codes.languageFrCaCode
                                                                    ? codes.languageFrCaCode
                                                                    : codes.languageEnCaCode,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            // Terms and Conditions section
                                            termsAndConditions && (
                                                <>
                                                    <div
                                                        ref={scrollContainerRef}
                                                        className={
                                                            styles['text']
                                                        }
                                                    >
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: termsAndConditions?.title,
                                                            }}
                                                        />
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: termsAndConditions?.body,
                                                            }}
                                                        />
                                                        <div
                                                            ref={endOfTermsRef}
                                                            className={
                                                                styles[
                                                                'end-of-terms'
                                                                ]
                                                            }
                                                            aria-hidden="true"
                                                        />
                                                    </div>
                                                    <div
                                                        className={
                                                            styles[
                                                            'modal-footer-button'
                                                            ]
                                                        }
                                                    >
                                                        <Button
                                                            variant="primary"
                                                            label={
                                                                termsAndConditions?.agreeButtonText
                                                            }
                                                            type={'submit'}
                                                            disabled={
                                                                !userHasReviewed
                                                            }
                                                            arrowDirection="right"
                                                            onClick={
                                                                handleAgreeClick
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            )
                                        )}
                                    </ModalBody>
                                </ModalContent>
                            </ModalDialog>
                        </Modal>
                    </div>
                </>
            ) : null}
        </>
    );
};

export default TermsWithLanguageModal;
